/**
 * Weekly recap (RPT-06) on the contract model (D112 #6): the dates whose
 * latest filing was submitted in the seven days before today, each with its
 * own status, plus the on-time share pangaea counts over those same rows
 * (`on_time`: approved on time ÷ approved with timing known). Outstanding
 * overdue is its own read, every late date: it is never part of the
 * percentage.
 *
 * The range is calendar days ("YYYY-MM-DD"), so nothing passes through a
 * time zone: the week the viewer reads is the week the request names.
 */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraPageHeader, SpecraSectionHeader, SpecraText, specraButtonVariants } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Link } from 'react-router-dom';
import { EmptyState } from '../../_shared/components/PageStates';
import { CmStatusBadge, CmTable } from '../../Deliverables/components/common';
import { OccurrenceWindow, useDuties, useOccurrences, useWorkstreams } from '../../Deliverables/hooks';
import { addDays, cmDate, todayISO } from '../../Deliverables/lib/format';
import { occurrenceStatus, onTimePercent } from '../../Deliverables/lib/status';
function plural(n, one) {
    return `${n} ${one}${n === 1 ? '' : 's'}`;
}
/** The filing's timeliness in words; a filing not yet approved has no verdict yet. */ function timeliness(o) {
    const st = o.status;
    if (!st?.timingKnown) return 'Not yet known';
    if (st.onTime) return 'On time';
    return `${plural(st.daysLate, 'day')} late`;
}
export function WeeklyRecapTab({ programId }) {
    const base = `/delivery/programs/${programId}`;
    const today = todayISO();
    const from = addDays(today, -7);
    const to = addDays(today, -1);
    const recap = useOccurrences(programId, {
        window: OccurrenceWindow.ALL,
        submittedFrom: from,
        submittedTo: to
    });
    const overdue = useOccurrences(programId, {
        window: OccurrenceWindow.ALL,
        states: [
            'late'
        ]
    });
    const duties = useDuties(programId, {
        includeRules: true
    });
    const workstreams = useWorkstreams(programId);
    const dutyById = new Map((duties.data?.duties ?? []).map((d)=>[
            d.id,
            d
        ]));
    const teamById = new Map((workstreams.data ?? []).map((w)=>[
            w.id,
            w.name
        ]));
    const rows = [
        ...recap.data?.occurrences ?? []
    ].sort((a, b)=>(a.latestSubmission?.submittedOn ?? '').localeCompare(b.latestSubmission?.submittedOn ?? '') || a.id.localeCompare(b.id));
    const overdueRows = [
        ...overdue.data?.occurrences ?? []
    ].sort((a, b)=>(a.dueOn || '').localeCompare(b.dueOn || '') || a.id.localeCompare(b.id));
    const counts = recap.data?.onTime;
    const pct = onTimePercent(counts);
    const deliverableCol = {
        id: 'deliverable',
        header: 'Deliverable',
        cell: (o)=>/*#__PURE__*/ _jsxs(Link, {
                to: `${base}/deliverables/${o.dutyId}`,
                className: "hover:underline",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-medium",
                        children: dutyById.get(o.dutyId)?.title || 'Untitled deliverable'
                    }),
                    o.label ? /*#__PURE__*/ _jsx("span", {
                        className: "ml-1.5 text-muted-foreground",
                        children: o.label
                    }) : null
                ]
            })
    };
    const teamCol = {
        id: 'team',
        header: 'Team',
        cell: (o)=>o.workstreamId ? teamById.get(o.workstreamId) ?? 'Unknown team' : 'Contract-wide'
    };
    const dueCol = {
        id: 'due',
        header: 'Due',
        cell: (o)=>/*#__PURE__*/ _jsx(Link, {
                to: `${base}/deliverables?occ=${o.id}`,
                className: "tabular-nums hover:underline",
                children: cmDate(o.dueOn)
            })
    };
    const statusCol = {
        id: 'status',
        header: 'Status',
        cell: (o)=>/*#__PURE__*/ _jsx(CmStatusBadge, {
                status: occurrenceStatus(o, dutyById.get(o.dutyId)?.finalReviewTier)
            })
    };
    const recapColumns = [
        deliverableCol,
        teamCol,
        dueCol,
        {
            id: 'submitted',
            header: 'Submitted',
            cell: (o)=>/*#__PURE__*/ _jsx("span", {
                    className: "tabular-nums",
                    children: cmDate(o.latestSubmission?.submittedOn)
                })
        },
        {
            id: 'timeliness',
            header: 'Timeliness',
            cell: timeliness
        },
        statusCol
    ];
    const figure = recap.isError ? /*#__PURE__*/ _jsx("span", {
        className: "text-muted-foreground",
        children: "On-time share couldn't load"
    }) : recap.isLoading ? /*#__PURE__*/ _jsx("span", {
        className: "text-muted-foreground",
        children: "Counting on-time share…"
    }) : pct === null ? /*#__PURE__*/ _jsx("span", {
        className: "text-muted-foreground",
        children: "No approvals with a known date yet"
    }) : /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("strong", {
                className: "font-medium tabular-nums text-foreground",
                children: [
                    pct,
                    "%"
                ]
            }),
            " on time (",
            counts?.onTime,
            " of ",
            counts?.timingKnown,
            " approved)"
        ]
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-6",
        "data-testid": Testids.weeklyRecap.section,
        children: [
            /*#__PURE__*/ _jsx(SpecraPageHeader, {
                actions: /*#__PURE__*/ _jsx(SpecraText, {
                    as: "div",
                    "data-testid": Testids.weeklyRecap.onTimeFigure,
                    size: "body-ui",
                    children: figure
                }),
                className: "sm:items-center",
                description: /*#__PURE__*/ _jsxs(SpecraText, {
                    "data-testid": Testids.weeklyRecap.rangeLabel,
                    size: "body-ui",
                    tone: "muted",
                    children: [
                        "Submitted ",
                        cmDate(from),
                        " – ",
                        cmDate(to)
                    ]
                }),
                title: "Weekly Recap"
            }),
            /*#__PURE__*/ _jsx(CmTable, {
                caption: "Deliverables submitted in the last 7 days",
                rows: rows,
                columns: recapColumns,
                rowKey: (o)=>o.id,
                testId: Testids.weeklyRecap.table,
                rowTestId: Testids.weeklyRecap.row,
                isLoading: recap.isLoading,
                error: recap.error,
                onRetry: ()=>void recap.refetch(),
                empty: /*#__PURE__*/ _jsx("div", {
                    "data-testid": Testids.weeklyRecap.empty,
                    children: /*#__PURE__*/ _jsx(EmptyState, {
                        title: "Nothing submitted in the last 7 days",
                        description: "Filings recorded on a deliverable's date show here the week they are submitted.",
                        action: /*#__PURE__*/ _jsx(Link, {
                            to: `${base}/deliverables`,
                            className: specraButtonVariants({
                                variant: 'secondary',
                                size: 'sm'
                            }),
                            children: "Open Deliverables"
                        })
                    })
                })
            }),
            /*#__PURE__*/ _jsxs("section", {
                className: "space-y-2",
                "data-testid": Testids.weeklyRecap.outstandingSection,
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        action: /*#__PURE__*/ _jsx(SpecraText, {
                            size: "meta",
                            tone: "muted",
                            children: "Not counted in the on-time share"
                        }),
                        title: `Not yet submitted – late (${overdue.isLoading ? '…' : overdueRows.length})`,
                        variant: "group"
                    }),
                    /*#__PURE__*/ _jsx(CmTable, {
                        caption: "Late deliverables not yet submitted",
                        rows: overdueRows,
                        columns: [
                            deliverableCol,
                            teamCol,
                            dueCol,
                            statusCol
                        ],
                        rowKey: (o)=>o.id,
                        rowTestId: Testids.onTimeDrilldown.outstandingRow,
                        isLoading: overdue.isLoading,
                        error: overdue.error,
                        onRetry: ()=>void overdue.refetch(),
                        empty: /*#__PURE__*/ _jsx(SpecraText, {
                            as: "p",
                            size: "body-ui",
                            tone: "muted",
                            children: "Nothing is late."
                        })
                    })
                ]
            })
        ]
    });
}
